/**
 * I18n context and provider following existing context patterns (NavigationThemeContext).
 * Provides locale and t() to the tree. Locale is set from Layout (pageContext.locale).
 */

import React, { createContext, useContext, useLayoutEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { i18n as I18nInstance } from 'i18next';
import { getDefaultLocale, normalizeToLocaleCode } from '@config/i18n';

export interface I18nContextValue {
  locale: string;
  t: TFunction;
  i18n: I18nInstance;
  ready: boolean;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
  locale: string | undefined;
}

const I18N_NAMESPACES = ['common', 'seo', 'pricing'] as const;

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, locale }) => {
  const resolvedLocale = normalizeToLocaleCode(locale || getDefaultLocale());
  const { t, i18n, ready } = useTranslation(I18N_NAMESPACES, { lng: resolvedLocale });

  useLayoutEffect(() => {
    if (resolvedLocale && i18n.language !== resolvedLocale) {
      i18n.changeLanguage(resolvedLocale);
    }
  }, [resolvedLocale, i18n]);

  const value: I18nContextValue = {
    locale: resolvedLocale,
    t,
    i18n,
    ready,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * Use i18n (locale and t) within the app. Must be used inside I18nProvider.
 */
export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
